import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable } from "mobx";
import { toastModule } from "@/modules/toast";

type ConflictModalState = {
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
};

export class ActiveRecordingConflictModalStore extends AppSubStore {
  private state: ConflictModalState | null = null;
  private isConfirmingValue = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<this, "state" | "isConfirmingValue">(this, {
      state: observable.ref,
      isConfirmingValue: observable,
      isOpen: computed,
      isConfirming: computed,
      open: action,
      close: action,
      handleConfirm: action,
      handleClose: action,
      title: computed,
      message: computed,
      confirmLabel: computed,
    });
  }

  get title(): string {
    return "Voice Mode is already in progress";
  }

  get message(): string {
    return "Do you want to end your current recording to start a new one?";
  }

  get confirmLabel(): string {
    return "Start New Recording";
  }

  get isOpen(): boolean {
    return Boolean(this.state);
  }

  get isConfirming(): boolean {
    return this.isConfirmingValue;
  }

  open(state: ConflictModalState): void {
    this.state = state;
    this.isConfirmingValue = false;
  }

  close(): void {
    if (!this.isOpen) return;

    this.state = null;
    this.isConfirmingValue = false;
  }

  async handleConfirm(): Promise<void> {
    const currentState = this.state;
    if (!currentState) return;
    if (this.isConfirmingValue) return;

    this.isConfirmingValue = true;

    try {
      await currentState.onConfirm?.();
      this.close();
    } catch (unknownErr) {
      logger.warn({
        message: "[ActiveRecordingConflictModalStore] handleConfirm failed",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });

      toastModule.triggerToast({
        content: "Something went wrong. Please try again.",
        toastId: "active-recording-conflict-modal-error",
      });

      this.isConfirmingValue = false;
    }
  }

  handleClose(): void {
    if (this.isConfirmingValue) return;
    const currentState = this.state;
    this.close();
    currentState?.onCancel?.();
  }
}
